import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Card, Col, Input, Modal, Row, Select, Space, Statistic, Table, Tag, Tooltip, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { ReloadOutlined } from '@ant-design/icons';
import apiClient from '../services/apiClient';
import './AdminRecommendations.css';

type RecommendationStatus = 'open' | 'dismissed' | 'implemented' | 'resolved';
type RecommendationProvider = 'aws' | 'azure' | 'gcp';

interface Recommendation {
  id: string;
  cloudAccountId: string;
  provider: RecommendationProvider;
  category: string;
  resourceId?: string;
  resourceName?: string;
  title: string;
  service?: string;
  currency: 'USD' | 'BRL';
  monthlyImpact: number;
  status: RecommendationStatus;
  firstSeenAt: string;
  lastSeenAt: string;
  sightingCount: number;
  statusNote?: string;
}

interface TotalsBucket {
  identifiedMonthly: number;
  implementedMonthly: number;
  openCount: number;
  resolvedCount: number;
  implementedCount: number;
  dismissedCount: number;
  currency?: string;
}

const statusLabel: Record<RecommendationStatus, string> = {
  open: 'Em aberto',
  dismissed: 'Dispensada',
  implemented: 'Implementada',
  resolved: 'Resolvida (auto)'
};

const statusColor: Record<RecommendationStatus, string> = {
  open: 'gold',
  dismissed: 'default',
  implemented: 'green',
  resolved: 'blue'
};

const providerLabel: Record<RecommendationProvider, string> = {
  aws: 'AWS',
  azure: 'Azure',
  gcp: 'GCP'
};

const providerColor: Record<RecommendationProvider, string> = {
  aws: 'orange',
  azure: 'geekblue',
  gcp: 'volcano'
};

const statusOptions: { value: RecommendationStatus; label: string }[] = [
  { value: 'open', label: 'Em aberto' },
  { value: 'dismissed', label: 'Dispensada' },
  { value: 'implemented', label: 'Implementada' }
];

const formatMoney = (value: number, currency: string) => {
  const locale = currency === 'USD' ? 'en-US' : 'pt-BR';
  return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(Number(value || 0));
};

const dateFormatter = (value?: string) => (value ? new Date(value).toLocaleString('pt-BR') : '-');

const AdminRecommendations: React.FC = () => {
  const [items, setItems] = useState<Recommendation[]>([]);
  const [nextToken, setNextToken] = useState<string | null>(null);
  const [pageHistory, setPageHistory] = useState<(string | null)[]>([null]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<RecommendationStatus | undefined>(undefined);
  const [providerFilter, setProviderFilter] = useState<RecommendationProvider | undefined>(undefined);
  const [totals, setTotals] = useState<Record<string, TotalsBucket>>({});
  const [editing, setEditing] = useState<Recommendation | null>(null);
  const [newStatus, setNewStatus] = useState<RecommendationStatus>('implemented');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  const currentToken = pageHistory[pageHistory.length - 1] ?? null;

  const loadRecommendations = useCallback(async (token: string | null) => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.get('/recommendations', {
        params: {
          status: statusFilter,
          provider: providerFilter,
          nextToken: token || undefined,
          limit: 25
        }
      });
      setItems(response.data?.recommendations || []);
      setNextToken(response.data?.pagination?.nextToken || null);
    } catch (loadError: any) {
      setError(loadError.response?.data?.error || 'Não foi possível carregar as recomendações.');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, providerFilter]);

  const loadTotals = useCallback(async () => {
    try {
      const response = await apiClient.get('/recommendations/summary');
      setTotals(response.data?.totals || {});
    } catch (loadError) {
      // Totais são um complemento visual - falha aqui não deve travar a tela.
      console.error('Erro ao carregar totais de recomendações:', loadError);
    }
  }, []);

  useEffect(() => {
    setPageHistory([null]);
  }, [statusFilter, providerFilter]);

  useEffect(() => {
    loadRecommendations(currentToken);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentToken, loadRecommendations]);

  useEffect(() => {
    loadTotals();
  }, [loadTotals]);

  const handleNextPage = () => {
    if (!nextToken) return;
    setPageHistory(prev => [...prev, nextToken]);
  };

  const handlePrevPage = () => {
    if (pageHistory.length <= 1) return;
    setPageHistory(prev => prev.slice(0, -1));
  };

  const openStatusModal = (record: Recommendation) => {
    setEditing(record);
    setNewStatus(record.status === 'open' ? 'implemented' : 'open');
    setNote('');
  };

  const closeStatusModal = () => {
    setEditing(null);
    setNote('');
  };

  const handleConfirmStatus = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      await apiClient.patch(`/recommendations/${editing.id}/status`, {
        status: newStatus,
        note: note || undefined
      });
      message.success('Status atualizado.');
      closeStatusModal();
      await Promise.all([loadRecommendations(currentToken), loadTotals()]);
    } catch (saveError: any) {
      message.error(saveError.response?.data?.error || 'Erro ao atualizar status.');
    } finally {
      setSaving(false);
    }
  };

  const totalsSummary = useMemo(() => {
    return Object.entries(totals).map(([currency, bucket]) => ({ currency, ...bucket }));
  }, [totals]);

  const columns: ColumnsType<Recommendation> = [
    {
      title: 'Recomendação',
      dataIndex: 'title',
      key: 'title',
      render: (_: string, record) => (
        <div>
          <div style={{ fontWeight: 600 }}>{record.title}</div>
          <div style={{ fontSize: 12, color: 'var(--ant-color-text-tertiary, #8c8c8c)' }}>
            {record.resourceName || record.resourceId || '-'}
          </div>
        </div>
      )
    },
    {
      title: 'Provider',
      dataIndex: 'provider',
      key: 'provider',
      width: 100,
      render: (provider: RecommendationProvider) => <Tag color={providerColor[provider]}>{providerLabel[provider]}</Tag>,
      filters: [
        { text: 'AWS', value: 'aws' },
        { text: 'Azure', value: 'azure' },
        { text: 'GCP', value: 'gcp' }
      ],
      onFilter: (value, record) => record.provider === value
    },
    {
      title: 'Serviço',
      dataIndex: 'service',
      key: 'service',
      render: (value?: string) => value || '-'
    },
    {
      title: 'Categoria',
      dataIndex: 'category',
      key: 'category'
    },
    {
      title: 'Impacto mensal',
      dataIndex: 'monthlyImpact',
      key: 'monthlyImpact',
      align: 'right',
      sorter: (a, b) => (a.monthlyImpact || 0) - (b.monthlyImpact || 0),
      render: (value: number, record) => formatMoney(value, record.currency)
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: RecommendationStatus) => <Tag color={statusColor[status]}>{statusLabel[status]}</Tag>
    },
    {
      title: 'Última vez vista',
      dataIndex: 'lastSeenAt',
      key: 'lastSeenAt',
      render: dateFormatter
    },
    {
      title: 'Vista em',
      dataIndex: 'sightingCount',
      key: 'sightingCount',
      align: 'center',
      width: 90,
      render: (value: number) => <Tooltip title="Quantidade de relatórios em que essa recomendação apareceu">{value}× relatório(s)</Tooltip>
    },
    {
      title: 'Ações',
      key: 'actions',
      fixed: 'right',
      width: 140,
      render: (_, record) => (
        <Button size="small" onClick={() => openStatusModal(record)}>
          Revisar
        </Button>
      )
    }
  ];

  return (
    <div className="admin-recommendations">
      <div className="admin-recommendations__header">
        <div>
          <h2>Gestão de Recomendações</h2>
          <p>Revise as oportunidades identificadas pela xKnow, confirme implementações e dispense falsos-positivos.</p>
        </div>
        <Button icon={<ReloadOutlined />} onClick={() => { loadRecommendations(currentToken); loadTotals(); }} loading={loading}>
          Atualizar
        </Button>
      </div>

      {error && <Alert type="error" message={error} showIcon style={{ marginBottom: 16 }} />}

      <Row gutter={16} style={{ marginBottom: 16 }}>
        {totalsSummary.length === 0 && (
          <Col span={24}>
            <Card size="small">
              <Statistic title="Total identificado" value={0} />
            </Card>
          </Col>
        )}
        {totalsSummary.map(bucket => (
          <React.Fragment key={bucket.currency}>
            <Col xs={12} md={6}>
              <Card size="small">
                <Statistic
                  title={`Identificado (${bucket.currency})`}
                  value={bucket.identifiedMonthly}
                  precision={2}
                  suffix={`/mês`}
                />
              </Card>
            </Col>
            <Col xs={12} md={6}>
              <Card size="small">
                <Statistic
                  title={`Implementado (${bucket.currency})`}
                  value={bucket.implementedMonthly}
                  precision={2}
                  suffix={`/mês`}
                  valueStyle={{ color: '#3f8600' }}
                />
              </Card>
            </Col>
          </React.Fragment>
        ))}
      </Row>

      <Space style={{ marginBottom: 16 }} wrap>
        <Select
          allowClear
          placeholder="Status"
          style={{ width: 180 }}
          value={statusFilter}
          onChange={setStatusFilter}
          options={[
            { value: 'open', label: 'Em aberto' },
            { value: 'dismissed', label: 'Dispensada' },
            { value: 'implemented', label: 'Implementada' },
            { value: 'resolved', label: 'Resolvida (auto)' }
          ]}
        />
        <Select
          allowClear
          placeholder="Provider"
          style={{ width: 140 }}
          value={providerFilter}
          onChange={setProviderFilter}
          options={[
            { value: 'aws', label: 'AWS' },
            { value: 'azure', label: 'Azure' },
            { value: 'gcp', label: 'GCP' }
          ]}
        />
      </Space>

      <Table
        rowKey="id"
        columns={columns}
        dataSource={items}
        loading={loading}
        pagination={false}
        scroll={{ x: 1100 }}
      />

      <div className="admin-recommendations__pager">
        <Button disabled={pageHistory.length <= 1} onClick={handlePrevPage}>
          Página anterior
        </Button>
        <Button disabled={!nextToken} onClick={handleNextPage} type="primary" ghost>
          Próxima página
        </Button>
      </div>

      <Modal
        title="Revisar recomendação"
        open={!!editing}
        onCancel={closeStatusModal}
        onOk={handleConfirmStatus}
        confirmLoading={saving}
        okText="Salvar"
        cancelText="Cancelar"
      >
        {editing && (
          <div className="admin-recommendations__modal-body">
            <p><strong>{editing.title}</strong></p>
            <p style={{ color: '#8c8c8c' }}>{editing.resourceName || editing.resourceId || '-'} · {providerLabel[editing.provider]}</p>
            <Select
              style={{ width: '100%', marginBottom: 12 }}
              value={newStatus}
              onChange={setNewStatus}
              options={statusOptions}
            />
            <Input.TextArea
              placeholder="Observação (opcional) - ex: motivo de dispensa, ticket relacionado"
              rows={3}
              value={note}
              onChange={event => setNote(event.target.value)}
              maxLength={500}
            />
          </div>
        )}
      </Modal>
    </div>
  );
};

export default AdminRecommendations;
